import { useTranslation } from "react-i18next";
import { apiClient } from "../../../services/api";
import {
  displayToast,
  displayToastWithActions,
} from "../../../components/app/AppToast";
import {
  reloadCurrentUser,
  useCurrentUser,
} from "../../user/hooks/useCurrentUser";
import {
  gaEventPinRoom,
  gaEventUnpinRoom,
} from "../../../tools/analytics/domains/room.analytics";

/**
 * The reader's pinned room on a desktop (Figma "04 - Rooms / Épingler une
 * Room"): one room at most, first in Rooms and the home page on every device
 * (`resolveHomeSurface`, unless a rule there sends the reader to JuisciGPT).
 * Pinning another room replaces the current one without asking, and the
 * toast's Undo puts back the pin from before the click, the previous room or
 * none.
 *
 * The same `users.pinnedRoom` as the phone's long press in `RoomsCenterPage`:
 * a pin set here shows there too. Leaving the room clears it server-side.
 */
export function usePinRoom(): {
  pinnedRoomId: string | null;
  pin: (room: { id: string; name: string }) => Promise<void>;
  unpin: (room: { id: string; name: string }) => Promise<void>;
} {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const pinnedRoomId = currentUser === null ? null : currentUser.pinnedRoom;

  async function writePinnedRoom(
    roomId: string | null
  ): Promise<"done" | "failed"> {
    try {
      if (roomId === null)
        await apiClient.delete("/webapp/private/users/me/pinned-room", {});
      else
        await apiClient.post("/webapp/private/users/me/pinned-room", {
          json: { roomId },
        });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return "failed";
    }
    await reloadCurrentUser();
    return "done";
  }

  async function pin(room: { id: string; name: string }): Promise<void> {
    const previous = pinnedRoomId;
    if ((await writePinnedRoom(room.id)) === "failed") return;
    gaEventPinRoom({ roomId: room.id, roomName: room.name });
    displayToastWithActions(
      t("{{room}} is pinned to top", { room: room.name }),
      "success",
      [
        {
          label: t("Undo"),
          onClick: () => {
            void writePinnedRoom(previous);
          },
        },
      ]
    );
  }

  async function unpin(room: { id: string; name: string }): Promise<void> {
    if ((await writePinnedRoom(null)) === "failed") return;
    gaEventUnpinRoom({ roomId: room.id, roomName: room.name });
    displayToastWithActions(
      t("{{room}} is unpinned", { room: room.name }),
      "success",
      [
        {
          label: t("Undo"),
          onClick: () => {
            void writePinnedRoom(room.id);
          },
        },
      ]
    );
  }

  return { pinnedRoomId, pin, unpin };
}
